import _ from "lodash";
import { makeStyles } from "tss-react/mui";

import { Theme, Tooltip } from "@mui/material";
import { CustomBadge } from "@shared/CustomBadge";

const useStyles = makeStyles()((theme: Theme) => ({
    artifact: {
        display: "inline-flex",
        alignItems: "center",
        marginRight: theme.spacing(1.5),
        color: theme.palette.emteria.primary,
    },
    artifactIcon: {
        fontSize: theme.typography.pxToRem(18),
        lineHeight: 1,
    },
    artifactBadge: {
        display: "inline",
    },
    tooltipContent: {
        display: "flex",
        flexDirection: "column",
        fontSize: theme.typography.pxToRem(14),
        padding: theme.spacing(1),
    },
    tooltipList: {
        margin: theme.spacing(1, 0, 0),
        paddingLeft: theme.spacing(2),
    },
}));

export const getArtifactIcon = (artifact: API.BuildArtifact) => {
    switch (artifact.type) {
        case "ota":
            return {
                icon: "fas fa-cloud-download-alt",
                tooltip: "OS update package",
            };
        case "os-firmware":
            return {
                icon: "fas fa-microchip",
                tooltip: "Initial device firmware",
            };
        case "signed-files":
            return {
                icon: "fas fa-file-signature",
                tooltip: "Signed intermediate files",
            };
        case "manifest":
            return {
                icon: "fas fa-file-alt",
                tooltip: "Build revision trace",
            };
        case "report":
            return {
                icon: "fa-solid fa-newspaper",
                tooltip: "Reporting document",
            };
        default:
            return {
                icon: "fas fa-certificate",
                tooltip: artifact.type,
            };
    }
};

const artifactsOrder = ["os-firmware", "ota", "signed-files", "manifest"];

type ArtifactType = API.BuildArtifact["type"];

export const Artifacts = (props: { artifacts: API.BuildArtifact[] }) => {
    const { classes } = useStyles();
    const uploadedArtifacts = props.artifacts.filter((item) => item.isUploaded);
    // Sort by name first, then stably by type, so the names listed in each type's tooltip keep a predictable order
    const namedArtifacts = [...uploadedArtifacts].sort((a, b) =>
        (a.name ?? "").localeCompare(b.name ?? "", undefined, { numeric: true, sensitivity: "base" }),
    );
    const orderedArtifacts = _.sortBy(namedArtifacts, (item) => artifactsOrder.indexOf(item.type));
    const artifactsByType = orderedArtifacts.reduce<Partial<Record<ArtifactType, API.BuildArtifact[]>>>((acc, artifact) => {
        const artifactArray = acc[artifact.type] ?? [];
        artifactArray.push(artifact);
        acc[artifact.type] = artifactArray;
        return acc;
    }, {});
    const uniqueArtifactsByType = _.uniqBy(orderedArtifacts, "type");

    return (
        <>
            {uniqueArtifactsByType.map((artifactOfType) => {
                const artifactsGroup = artifactsByType[artifactOfType.type];
                if (!artifactsGroup?.length) {
                    return null;
                }
                const { icon, tooltip } = getArtifactIcon(artifactOfType);
                const tooltipContent = (
                    <div className={classes.tooltipContent}>
                        <div>{tooltip}</div>
                        <ul className={classes.tooltipList}>
                            {artifactsGroup.map((artifact, index) => (
                                <li key={`${artifact.type}-${artifact.id}-${index}`}>{artifact.name ?? ""}</li>
                            ))}
                        </ul>
                    </div>
                );

                return (
                    <div key={artifactOfType.type} className={classes.artifact}>
                        <Tooltip arrow placement="top" title={tooltipContent}>
                            <CustomBadge
                                badgeContent={artifactsGroup.length}
                                invisible={artifactsGroup.length <= 1}
                                className={classes.artifactBadge}
                                overlap="rectangular"
                            >
                                <i className={`${icon} ${classes.artifactIcon}`} />
                            </CustomBadge>
                        </Tooltip>
                    </div>
                );
            })}
        </>
    );
};
